// App.tsx
import React, { useState, useRef, useEffect } from 'react';
import { marked } from 'marked';
import Prism from 'prismjs';
import 'prismjs/themes/prism.css';
import { UsageInfo } from './types';
import UsageDisplay from './UsageDisplay';
import LoadingSpinner from './LoadingSpinner';
import SubscriptionDialog from './SubscriptionDialog';
import SubscriptionManagement from './SubscriptionManagement';
import PaymentSuccessHandler from './PaymentSuccessHandler';
import OptimizedLoginForm from './OptimizedLoginForm';
import AdminDashboard from './AdminDashboard';
import MessageContent from './MessageContent';
import RateLimitMessage from './RateLimitMessage';

// Types from backend
interface User {
  id: string;
  encryptedEmail: string; // Changed from email to encryptedEmail
  decryptedEmail?: string; // Add optional decrypted email
  createdAt: number;
  tier: 'free' | 'beta' | 'alpha';  // Add tier to User interface
}

interface Conversation {
  id: string;
  userId: string;
  title: string;
  createdAt: number;
  lastMessageAt: number;
}

interface Message {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface LoginFormProps {
  onSubmit: (email: string, captchaToken: string) => Promise<void>;
  isLoading: boolean;
}
interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

interface ConversationState {
  conversations: Conversation[];
  selectedConversationId: string | null;
  isLoading: boolean;
  error: string | null;
}

interface ChatState {
  messages: Message[];
  currentMessage: string;
  isLoading: boolean;
  error: string | null;
  isRateLimited: boolean;
}

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
}

// In App.tsx, add a RateLimitMessage componentL
interface RateLimitMessageProps {
  usage: UsageInfo;
  onUpgradeClick: () => void;
}

declare global {
  interface Window {
    turnstile: {
      render: (element: HTMLElement | string, options: any) => void;
      reset: (element: HTMLElement | null) => void;
    };
  }
}

// Components

// 2. Add responsive navigation component for mobile
const MobileNav: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ isOpen, onClose, children }) => {
  return (
    <div 
      className={`fixed inset-0 z-40 lg:hidden ${isOpen ? '' : 'hidden'}`}
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900/80" 
        onClick={onClose}
      />
      
      {/* Sliding panel */}
      <div className="fixed inset-y-0 left-0 w-64 bg-slate-800">
        <div className="h-full overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel"
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900/80" 
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className="relative bg-slate-800 rounded-lg shadow-lg max-w-md w-full mx-4 border border-slate-700">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-slate-100 mb-2">
            {title}
          </h3>
          <p className="text-slate-300 mb-6">
            {description}
          </p>
          
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md bg-slate-700 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              {cancelText}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Update ConversationList component props and implementation
const ConversationList: React.FC<{
  conversations: Conversation[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onRename: (id: string, newTitle: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}> = ({ conversations, selectedId, onSelect, onNew, onRename, onDelete }) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleSubmitRename = async (e: React.FormEvent, id: string) => {
    e.preventDefault();
    try {
      await onRename(id, editTitle);
      setEditingId(null);
    } catch (error) {
      console.error('Failed to rename:', error);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-800">
      <div className="flex-none p-4">
        <button
          onClick={onNew}
          className="w-full py-2 px-4 bg-emerald-500 text-slate-900 rounded-md hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 gap-2 flex items-center justify-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          New Chat
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {conversations.map((conv) => (
          <div
            key={conv.id}
            className={`group relative px-4 py-3 hover:bg-slate-700 ${
              selectedId === conv.id ? 'bg-slate-700' : ''
            }`}
          >
            {editingId === conv.id ? (
              <form
                onSubmit={(e) => handleSubmitRename(e, conv.id)}
                className="flex items-center gap-2"
              >
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="flex-1 bg-slate-600 text-slate-100 px-2 py-1 rounded"
                  autoFocus
                />
                <button
                  type="submit"
                  className="text-emerald-500 hover:text-emerald-400"
                >
                  Save
                </button>
              </form>
            ) : (
              <>
                <button
                  onClick={() => onSelect(conv.id)}
                  className="w-full text-left"
                >
                  <div className="text-sm font-medium text-slate-200 truncate">
                    {conv.title || 'New Conversation'}
                  </div>
                  <div className="text-xs text-slate-400">
                    {new Date(conv.lastMessageAt).toLocaleDateString()}
                  </div>
                </button>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden group-hover:flex gap-1">
                  <button
                    onClick={() => {
                      setEditingId(conv.id);
                      setEditTitle(conv.title);
                    }}
                    className="p-1 text-slate-400 hover:text-slate-200"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setDeleteId(conv.id)}
                    className="p-1 text-slate-400 hover:text-red-400"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      
      <ConfirmationDialog
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={() => {
          if (deleteId) {
            onDelete(deleteId).then(() => setDeleteId(null));
          }
        }}
        title="Delete Conversation"
        description="Are you sure you want to delete this conversation? This action cannot be undone."
        confirmText="Delete"
      />
    </div>
  );
};

export default function App() {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);
  const [isSubscriptionManagementOpen, setIsSubscriptionManagementOpen] = useState(false);
  const handleSubscribe = async (planId: string, paymentMethod: 'stripe' | 'bch') => {
    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ planId, paymentMethod })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { url } = await response.json();
      
      // Redirect to checkout
      window.location.href = url;
    } catch (error) {
      console.error('Subscription error:', error);
      // Handle error (show error message to user)
    }
  };

  // Add function to handle upgrade button click
  const handleUpgradeClick = () => {
    setIsSubscriptionDialogOpen(true);
  };

  // Update the UsageDisplay component in the header to include upgrade button
  const renderUsageWithUpgrade = () => (
    <div className="flex items-center gap-4">
      {usageInfo && (
      <UsageDisplay 
        usage={usageInfo} 
        setIsSubscriptionDialogOpen={setIsSubscriptionDialogOpen}
      />
  )}
    </div>
  );

  // Add function to fetch usage info
  const fetchUsageInfo = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/usage`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
        }
      });
    
      if (!response.ok) throw new Error('Failed to fetch usage info');
    
      const usage = await response.json();
      console.log('Usage info received:', usage); // Add this line
      setUsageInfo(usage);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: false,
    error: null,
    decryptedEmail: null
  });
  const [convState, setConvState] = useState<ConversationState>({
    conversations: [],
    selectedConversationId: null,
    isLoading: false,
    error: null
  });
  const [chatState, setChatState] = useState<ChatState>({
    messages: [],
    currentMessage: '',
    isLoading: false,
    error: null,
    isRateLimited: false
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminView, setIsAdminView] = useState(false);
  const fetchDecryptedEmail = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/auth/decrypt-email`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
        }
      });
  
      if (!response.ok) throw new Error('Failed to fetch decrypted email');
  
      const { decryptedEmail } = await response.json();
      setAuthState(prev => ({
        ...prev,
        decryptedEmail
      }));
    } catch (error) {
      console.error('Error fetching decrypted email:', error);
    }
  };
  const [usageInfo, setUsageInfo] = useState<UsageInfo | null>(null);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const mobileMenuButton = (
    <button
      type="button"
      className="lg:hidden inline-flex items-center p-2 rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-700 focus:outline-none"
      onClick={() => setIsMobileNavOpen(true)}
    >
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    </button>
  );

  const handleRenameConversation = async (id: string, newTitle: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/conversations/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title: newTitle })
      });

      if (!response.ok) throw new Error('Failed to rename conversation');

      const updatedConversation = await response.json();
      setConvState(prev => ({
        ...prev,
        conversations: prev.conversations.map(conv =>
          conv.id === id ? updatedConversation : conv
        )
      }));
    } catch (error) {
      console.error('Failed to rename conversation:', error);
      setConvState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to rename conversation'
      }));
    }
  };

  const handleDeleteConversation = async (id: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/conversations/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete conversation');

      setConvState(prev => {
        const newConversations = prev.conversations.filter(conv => conv.id !== id);
        return {
          ...prev,
          conversations: newConversations,
          selectedConversationId: id === prev.selectedConversationId 
            ? newConversations[0]?.id || null 
            : prev.selectedConversationId
        };
      });
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      setConvState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to delete conversation'
      }));
    }
  };

  useEffect(() => {
    const handleOpenSubscription = () => {
      setIsSubscriptionDialogOpen(true);
    };
  
    window.addEventListener('openSubscriptionDialog', handleOpenSubscription);
  
    return () => {
      window.removeEventListener('openSubscriptionDialog', handleOpenSubscription);
    };
  }, []);
  
  useEffect(() => {
    // Check for magic link token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      verifyToken(token);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Check for existing session
    const sessionToken = localStorage.getItem('sessionToken');
    if (sessionToken) {
      verifyToken(sessionToken);
    }

    // Set up markdown
    marked.setOptions({
      breaks: true,
      gfm: true
    });
  }, []);

  useEffect(() => {
    if (authState.user) {
      loadConversations();
    }
  }, [authState.user]);

  useEffect(() => {
    if (convState.selectedConversationId) {
      loadMessages(convState.selectedConversationId);
    }
  }, [convState.selectedConversationId]);

  useEffect(() => {
    scrollToBottom();
    if (typeof Prism !== 'undefined') {
      Prism.highlightAll();
    }
  }, [chatState.messages]);

  useEffect(() => {
    if (authState.user) {
      loadConversations();
      fetchUsageInfo();
    }
  }, [authState.user]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleLogin = async (email: string, captchaToken: string) => {
    console.log('Login attempt with email:', email);
    console.log('Worker URL:', import.meta.env.VITE_WORKER_URL);
  
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const url = `${import.meta.env.VITE_WORKER_URL}/auth/login`;
      console.log('Making request to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify({ 
          email,
          captchaToken  // Add this line
        })
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);
      
      if (!response.ok) throw new Error('Login failed');
      
      setAuthState(prev => ({
        ...prev,
        isLoading: false
      }));
    } catch (error) {
      console.error('Login error:', error);
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Login failed'
      }));
    }
  };
  
  const verifyToken = async (token: string) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      
      if (!response.ok) throw new Error('Invalid token');
      
      const data = await response.json();
      localStorage.setItem('sessionToken', data.token);
      
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        user: data.user
      }));
  
      // Fetch decrypted email after successful verification
      await fetchDecryptedEmail();
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Verification failed'
      }));
      localStorage.removeItem('sessionToken');
    }
  };

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!authState.user) return;
      
      try {
        const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/auth/check-admin`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
          }
        });
        
        if (response.ok) {
          const { isAdmin } = await response.json();
          setIsAdmin(isAdmin);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };
  
    checkAdminStatus();
  }, [authState.user]);

  const loadConversations = async () => {
    setConvState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/conversations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to load conversations');
      
      const conversations = await response.json();
      setConvState(prev => ({
        ...prev,
        isLoading: false,
        conversations,
        selectedConversationId: conversations[0]?.id || null
      }));
  
      // If there's a conversation, load its messages
      if (conversations[0]?.id) {
        loadMessages(conversations[0].id);
      }
    } catch (error) {
      setConvState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load conversations'
      }));
    }
  };

  const createNewConversation = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/conversations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to create conversation');
      
      const conversation = await response.json();
      setConvState(prev => ({
        ...prev,
        conversations: [conversation, ...prev.conversations],
        selectedConversationId: conversation.id
      }));
      setChatState(prev => ({
        ...prev,
        messages: [],
        currentMessage: ''
      }));
    } catch (error) {
      setConvState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to create conversation'
      }));
    }
  };

  const loadMessages = async (conversationId: string) => {
    setChatState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await fetch(
        `${import.meta.env.VITE_WORKER_URL}/conversations/${conversationId}/messages`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to load messages');
      
      const messages = await response.json();
      setChatState(prev => ({
        ...prev,
        messages,
        isLoading: false
      }));
    } catch (error) {
      setChatState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load messages'
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatState.currentMessage.trim() || !convState.selectedConversationId) return;
  
    const message = chatState.currentMessage;
    setChatState(prev => ({
      ...prev,
      currentMessage: '',
      messages: [...prev.messages, {
        id: 'temp-' + Date.now(),
        conversationId: convState.selectedConversationId!,
        role: 'user',
        content: message,
        timestamp: Date.now()
      }]
    }));
  
    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: message,
          conversationId: convState.selectedConversationId
        })
      });
  
      if (response.status === 403) {
        const errorData = await response.json();
        setChatState(prev => ({
          ...prev,
          error: errorData.error,
          isRateLimited: true
        }));
        // Refresh usage info when limit is reached
        await fetchUsageInfo();
        return;
      }
  
      if (!response.ok) throw new Error('Failed to send message');
      if (!response.body) throw new Error('No response body');
  
      // Reset rate limited state if request succeeds
      setChatState(prev => ({
        ...prev,
        isRateLimited: false
      }));
  
      // Refresh usage info after successful message
      await fetchUsageInfo();
  
      // Add empty assistant message that will be populated by the stream
      setChatState(prev => ({
        ...prev,
        messages: [...prev.messages, {
          id: 'temp-response-' + Date.now(),
          conversationId: convState.selectedConversationId!,
          role: 'assistant',
          content: '',
          timestamp: Date.now()
        }]
      }));
  
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
  
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
  
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
  
        for (const line of lines) {
          if (line.startsWith('data: ') && line.trim() !== 'data: [DONE]') {
            try {
              const data = JSON.parse(line.slice(5));
              if (data.content) {
                setChatState(prev => ({
                  ...prev,
                  messages: prev.messages.map((msg, index) => {
                    if (index === prev.messages.length - 1) {
                      return {
                        ...msg,
                        content: msg.content + data.content
                      };
                    }
                    return msg;
                  })
                }));
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setChatState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to send message',
        messages: prev.messages.slice(0, -1) // Remove the user message if there was an error
      }));
    }
  };

  if (!authState.user) {
    return <OptimizedLoginForm onSubmit={handleLogin} isLoading={authState.isLoading} />;
  }

  const handlePaymentSuccess = async () => {
    try {
      // First verify the session is still valid
      const token = localStorage.getItem('sessionToken');
      if (!token) {
        throw new Error('Session expired');
      }
  
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
  
      if (!response.ok) {
        throw new Error('Session verification failed');
      }
  
      const data = await response.json();
      
      // Update auth state with fresh user data
      setAuthState(prev => ({
        ...prev,
        user: data.user,
        isLoading: false,
        error: null
      }));
  
      // Now that we know the session is valid, refresh the app state
      await fetchUsageInfo();
      await loadConversations();
      
      alert('Your subscription has been activated successfully!');
    } catch (error) {
      console.error('Payment success handler error:', error);
      // If we hit an auth error, redirect to login
      localStorage.removeItem('sessionToken');
      setAuthState({
        user: null,
        isLoading: false,
        error: 'Session expired. Please log in again.'
      });
    }
  }; 

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Fixed Desktop Sidebar */}
      <aside className="hidden lg:block w-64 fixed left-0 top-0 bottom-0 border-r border-slate-700 z-30">
        <ConversationList
          conversations={convState.conversations}
          selectedId={convState.selectedConversationId}
          onSelect={(id) => {
            setConvState(prev => ({ ...prev, selectedConversationId: id }));
          }}
          onNew={createNewConversation}
          onRename={handleRenameConversation}
          onDelete={handleDeleteConversation}
        />
      </aside>

      {/* Mobile Navigation */}
      <MobileNav isOpen={isMobileNavOpen} onClose={() => setIsMobileNavOpen(false)}>
        <ConversationList
          conversations={convState.conversations}
          selectedId={convState.selectedConversationId}
          onSelect={(id) => {
            setConvState(prev => ({ ...prev, selectedConversationId: id }));
            setIsMobileNavOpen(false);
          }}
          onNew={createNewConversation}
          onRename={handleRenameConversation}
          onDelete={handleDeleteConversation}
        />
      </MobileNav>

{/* Main Content */}
<main className="flex-1 flex flex-col lg:ml-64">
<SubscriptionDialog
        isOpen={isSubscriptionDialogOpen}
        onClose={() => setIsSubscriptionDialogOpen(false)}
        onSubscribe={handleSubscribe}
        currentTier={authState.user?.tier || 'free'}
      />
<SubscriptionManagement
  isOpen={isSubscriptionManagementOpen}
  onClose={() => setIsSubscriptionManagementOpen(false)}
  currentTier={authState.user?.tier as 'beta' | 'alpha'}
/>
{window.location.pathname === '/payment-success' && (
    <PaymentSuccessHandler onSuccess={handlePaymentSuccess} />
  )}
{/* Header */}
<header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-20">
  <div className="w-full px-4 py-2">
    <div className="hidden lg:flex items-center justify-between">
      <div className="flex items-center gap-2">
        <img src="/android-chrome-192x192.png" alt="Logo" className="h-6 w-6" />
        <h1 className="text-lg font-bold text-slate-100">askchad.io</h1>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          {usageInfo && (
            <>
              <UsageDisplay 
                usage={usageInfo} 
                setIsSubscriptionDialogOpen={setIsSubscriptionDialogOpen}
              />
              {(authState.user?.tier === 'beta' || authState.user?.tier === 'alpha') ? (
                <button
                  onClick={() => setIsSubscriptionManagementOpen(true)}
                  className="text-emerald-500 hover:text-emerald-400 font-medium"
                >
                  Manage Plan
                </button>
              ) : null}
            </>
          )}
        </div>
        <div className="flex items-center gap-4 pl-4 border-l border-slate-600">
         <span className="text-slate-300">{authState.decryptedEmail}</span>
         {isAdmin && (
          <button
            onClick={() => setIsAdminView(!isAdminView)}
            className="text-slate-300 hover:text-slate-100"
          >
          {isAdminView ? 'Switch to Chat' : 'Admin Dashboard'}
          </button>
          )}
          <button
            onClick={() => {
              localStorage.removeItem('sessionToken');
              setAuthState({ user: null, isLoading: false, error: null });
            }}
            className="text-slate-400 hover:text-slate-200"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>

    {/* Mobile Header - Two Lines */}
    <div className="lg:hidden">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {mobileMenuButton}
          <img src="/android-chrome-192x192.png" alt="Logo" className="h-6 w-6" />
          <h1 className="text-lg font-bold text-slate-100">askchad.io</h1>
        </div>
        <button
          onClick={() => {
            localStorage.removeItem('sessionToken');
            setAuthState({ user: null, isLoading: false, error: null });
          }}
          className="text-slate-400 hover:text-slate-200"
        >
          Sign Out
        </button>
      </div>

      <div className="flex flex-col gap-2">
        {usageInfo && (
          <div className="flex items-center gap-2">
            <span className="text-slate-300">{authState.decryptedEmail}</span>
            <UsageDisplay 
              usage={usageInfo} 
              setIsSubscriptionDialogOpen={setIsSubscriptionDialogOpen}
            />
            {(authState.user?.tier === 'beta' || authState.user?.tier === 'alpha') ? (
              <button
                onClick={() => setIsSubscriptionManagementOpen(true)}
                className="text-emerald-500 hover:text-emerald-400 font-medium"
              >
                Manage Plan
                </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  </div>
</header>

  {/* Chat Area */}
  <div className="flex-1 relative">
  {isAdminView && isAdmin ? (
  <AdminDashboard />
) : (
  <div className="absolute inset-0 overflow-y-auto">
    {convState.isLoading || chatState.isLoading ? (
      <LoadingSpinner />
    ) : (
      <div className="p-4 space-y-4 pb-32">
{chatState.messages.map((message) => (
  <div 
    key={message.id}
    className={"flex " + (message.role === 'user' ? 'justify-end' : 'justify-start')}
  >
    <div 
      className={
        "max-w-[85%] rounded-lg p-4 " + (
          message.role === 'user' 
            ? 'bg-emerald-600 text-slate-100 shadow-lg' 
            : 'bg-slate-700 text-slate-100 shadow-lg'
        )
      }
    >
      <MessageContent 
        content={message.content} 
        isUser={message.role === 'user'} 
      />
    </div>
    </div>
))}
{chatState.isRateLimited && usageInfo && (
  <RateLimitMessage
    usage={usageInfo}
    onUpgradeClick={() => setIsSubscriptionDialogOpen(true)}
  />
)}
        <div ref={messagesEndRef} />
      </div>
    )}
  </div>
  )}
  </div>

          {/* Fixed Input Area */}
<div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-slate-800 border-t border-slate-700 p-4">
  <form onSubmit={handleSubmit} className="max-w-screen-2xl mx-auto flex space-x-4">
  <input
  type="text"
  value={chatState.currentMessage}
  onChange={(e) => setChatState(prev => ({
    ...prev,
    currentMessage: e.target.value
  }))}
  className="flex-1 min-w-0 rounded-md bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-400 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
  placeholder={chatState.isRateLimited ? "Rate limit reached. Please wait or upgrade." : "Type your message..."}
  disabled={chatState.isLoading || !convState.selectedConversationId || chatState.isRateLimited}
/>
<button
  type="submit"
  disabled={chatState.isLoading || !chatState.currentMessage.trim() || !convState.selectedConversationId || chatState.isRateLimited}
  className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-lg text-slate-900 bg-emerald-500 hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-800 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed [transition:background-color_150ms]"
>
  Send
</button>
  </form>
</div>
      </main>
    </div>
  );
}
